import type { Question, ResponsePayload } from 'types';
import { normalizeQuestionType } from './questionTypeUtils';

export interface EvaluationAnswer {
  text_value?: string;
  text_list?: string[];
}

export const supportsAnswerEvaluation = (questionType: string): boolean =>
  ['text_or_audio', 'text_list'].includes(normalizeQuestionType(questionType));

export const hasAnswerEvaluation = (question?: Question | null): boolean =>
  Boolean(question && supportsAnswerEvaluation(question.question_type_display ?? question.question_type)
    && question.metadata?.ai_evaluation?.enabled === true);

export const isSkippedAnswerEvaluation = (question: Question, response?: ResponsePayload | null): boolean => {
  const saved = response?.metadata?.ai_evaluation;
  return hasAnswerEvaluation(question) && response?.is_completed === true
    && Boolean(saved && typeof saved === 'object' && 'outcome' in saved && saved.outcome === 'skipped');
};

export const evaluationAnswer = (question: Question, response?: ResponsePayload | null): EvaluationAnswer =>
  normalizeQuestionType(question.question_type_display ?? question.question_type) === 'text_list'
    ? { text_list: response?.text_list ?? [] }
    : { text_value: response?.text_value ?? '' };

export const validateAnswerEvaluation = (
  metadata: Record<string, unknown> | undefined,
  questionType: string,
): string | null => {
  const value = metadata?.ai_evaluation;
  if (value === undefined) return null;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return 'Follow-up settings must be a JSON object.';
  }
  const config = value as Record<string, unknown>;
  if (Object.keys(config).some((key) => !['enabled', 'criteria', 'max_follow_ups'].includes(key))) {
    return 'Follow-up settings contain an unsupported field.';
  }
  if (typeof config.enabled !== 'boolean') return 'Follow-up settings require enabled to be true or false.';
  if (config.criteria !== undefined && typeof config.criteria !== 'string') return 'Follow-up criteria must be text.';
  if (typeof config.criteria === 'string' && Array.from(config.criteria).length > 6000) return 'Follow-up criteria must be 6,000 characters or fewer.';
  const limit = config.max_follow_ups === undefined ? 3 : config.max_follow_ups;
  if (typeof limit !== 'number' || !Number.isInteger(limit) || limit < 1 || limit > 5) {
    return 'The follow-up limit must be a whole number from 1 to 5.';
  }
  if (!config.enabled) return null;
  if (!supportsAnswerEvaluation(questionType)) return 'Follow-ups are available for Text or Audio and Text List questions only.';
  if (typeof config.criteria !== 'string' || !config.criteria.trim()) return 'Add criteria for a sufficiently answered question.';
  return null;
};
